import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { boldText, linkText, mailToLink } from '@utils/intlFormatterUtils';

interface ITermsAndConditions {
	header: string;
	text: string[] | ReactNode;
	multiLine: IMultiLevelConditions;
	cookies: ICookies;
}

interface IMultiLevelConditions {
	isMultiLevel: boolean;
	list?: string[];
	endText?: string[];
}

interface ICookies {
	isCookie: boolean;
	props: ICookieProps[];
}

interface ICookieProps {
	name?: string;
	functionality?: string;
	period?: string;
}

const PrivacyPolicyTextBlock = (props: any) => {
	return (
		<>
			{props.text.map((element: string, index: number) => {
				return (
					<p key={index} className='mt-3 w-full text-justify font-normal'>
						{element}
					</p>
				);
			})}
		</>
	);
};

const PrivacyPolicyCookies = (info: ICookies) => {
	return (
		<>
			{info.props.map((element: ICookieProps, index: number) => {
				return (
					<div key={index}>
						<p className='mt-3 w-full text-justify font-normal'>{element.name}</p>
						<p className='w-full text-justify font-normal'>{element.functionality}</p>
						<p className='w-full text-justify font-normal'>{element.period}</p>
					</div>
				);
			})}
		</>
	);
};

const PrivacyPolicyBlock = ({ header, text, multiLine, cookies }: ITermsAndConditions) => {
	return (
		<div className='pt-4'>
			<h2 className='text-xl mt-1 mb-2' style={{ fontFamily: "'Montserrat', normal" }}>
				{header}
			</h2>
			{text ? <PrivacyPolicyTextBlock text={text} /> : null}
			<ul className='ml-10 text-justify'>
				{multiLine.list?.map((element, index) => {
					return (
						<li className='list-disc' key={index}>
							{element}
						</li>
					);
				})}
			</ul>
			{multiLine?.endText ? <PrivacyPolicyTextBlock text={multiLine.endText} /> : null}
			{cookies.props ? <PrivacyPolicyCookies isCookie={true} props={cookies.props} /> : null}
		</div>
	);
};

const PrivacyPolicy = () => {
	const { formatMessage } = useIntl();
	return (
		<div className='container mb-5 mx-auto mt-22 flex justify-center'>
			<div className='lg:w-[70%] w-[95%]'>
				<div className='text-2xl 2xl:text-4.5xl' style={{ fontFamily: "'Montserrat', normal" }}>
					<h1 className='text-2xl mt-2 mb-4'>
						{formatMessage({ id: 'bcs-Privacy-Policy-Title' }, { b: boldText }) as string}
					</h1>
				</div>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-Privacy-Policy-Terms-Title' }, { b: boldText }) as string}
					text={[]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-I' }),
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-II' }),
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-III' }),
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-IV' }),
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-V' }),
							formatMessage({ id: 'bcs-Privacy-Policy-Terms-multilevel-VI' }),
						],
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-I-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[formatMessage({ id: 'bcs-I-Privacy-Policy-Text-I' })]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-II-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-II-Privacy-Policy-Text-I' }),
						formatMessage({ id: 'bcs-II-Privacy-Policy-Text-II' }),
						formatMessage({ id: 'bcs-II-Privacy-Policy-Text-III' }),
						formatMessage({ id: 'bcs-II-Privacy-Policy-Text-IV' }),
						formatMessage({ id: 'bcs-II-Privacy-Policy-Text-V' }, { mailToLink: mailToLink }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-III-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-III-Privacy-Policy-Text-I' }),
						formatMessage({ id: 'bcs-III-Privacy-Policy-Text-II' }),
						formatMessage({ id: 'bcs-III-Privacy-Policy-Text-III' }),
						formatMessage({ id: 'bcs-III-Privacy-Policy-Text-IV' }),
					]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-III-Privacy-Policy-multilevel-I' }),
							formatMessage({ id: 'bcs-III-Privacy-Policy-multilevel-II' }),
							formatMessage({ id: 'bcs-III-Privacy-Policy-multilevel-III' }),
							formatMessage({ id: 'bcs-III-Privacy-Policy-multilevel-IV' }),
							formatMessage({ id: 'bcs-III-Privacy-Policy-multilevel-V' }),
						],
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-IV-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-I' }),
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-II' }),
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-III' }),
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-IV' }),
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-V' }),
						formatMessage({ id: 'bcs-IV-Privacy-Policy-Text-VI' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-V-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[formatMessage({ id: 'bcs-V-Privacy-Policy-Text-I' })]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-V-Privacy-Policy-multilevel-I' }),
							formatMessage({ id: 'bcs-V-Privacy-Policy-multilevel-II' }),
							formatMessage({ id: 'bcs-V-Privacy-Policy-multilevel-III' }),
							formatMessage({ id: 'bcs-V-Privacy-Policy-multilevel-IV' }),
							formatMessage({ id: 'bcs-V-Privacy-Policy-multilevel-V' }),
						],
						endText: [
							formatMessage(
								{ id: 'bcs-V-Privacy-Policy-Text-III' },
								{ link: (text: any) => linkText(text, 'https://developers.facebook.com/terms/') }
							) as string,
						],
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-VI-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-VI-Privacy-Policy-Text-I' }),
						formatMessage({ id: 'bcs-VI-Privacy-Policy-Text-II' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-VII-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[formatMessage({ id: 'bcs-VII-Privacy-Policy-Text-I' })]}
					multiLine={{
						isMultiLevel: true,
						list: [
							formatMessage({ id: 'bcs-VII-Privacy-Policy-multilevel-I' }),
							formatMessage({ id: 'bcs-VII-Privacy-Policy-multilevel-II' }),
							formatMessage({ id: 'bcs-VII-Privacy-Policy-multilevel-III' }),
							formatMessage(
								{ id: 'bcs-VII-Privacy-Policy-multilevel-IV' },
								{ a: (text: any) => linkText(text, 'https://www.cpdp.bg'), mailToLink: mailToLink }
							) as string,
						],
						endText: [
							formatMessage(
								{ id: 'bcs-VII-Privacy-Policy-Text-II' },
								{ mailToLink: mailToLink }
							) as string,
						],
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-VIII-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[formatMessage({ id: 'bcs-VIII-Privacy-Policy-Text-I' })]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: false,
						props: [],
					}}
				/>
				<PrivacyPolicyBlock
					header={formatMessage({ id: 'bcs-IX-Privacy-Policy-Title' }, { b: boldText }) as string}
					text={[
						formatMessage({ id: 'bcs-IX-Privacy-Policy-Text-I' }),
						formatMessage({ id: 'bcs-IX-Privacy-Policy-Text-II' }),
						formatMessage({ id: 'bcs-IX-Privacy-Policy-Text-III' }),
					]}
					multiLine={{
						isMultiLevel: false,
					}}
					cookies={{
						isCookie: true,
						props: [
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-I-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-I-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-I-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-II-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-II-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-II-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-III-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-III-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-III-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IV-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IV-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IV-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-V-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-V-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-V-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VI-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VI-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VI-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VIII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VIII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-VIII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IX-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IX-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-IX-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-X-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-X-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-X-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XI-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XI-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XI-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIV-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIV-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIV-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XV-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XV-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XV-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVI-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVI-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVI-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVIII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVIII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XVIII-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIX-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIX-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XIX-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XX-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XX-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XX-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXI-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXI-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXI-III' }),
							},
							{
								name: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXII-I' }),
								functionality: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXII-II' }),
								period: formatMessage({ id: 'bcs-IX-Privacy-Policy-Cookies-XXII-III' }),
							},
						],
					}}
				/>
			</div>
		</div>
	);
};

export default PrivacyPolicy;
